import type { ReactNode } from 'react';

/**
 * Column width for the loading skeleton — the same width the board's columns
 * and drag overlay use, so the skeleton does not jump when the board loads.
 */
export const KANBAN_COLUMN_WIDTH = 'w-[296px]';

/** A board column (a status, a SELECT option, …). */
export interface KanbanColumn {
    id: string;
    label: string;
    /** Accent color (hex) for the column header dot. */
    color?: string;
    /** Total items in this column server-side (may exceed loaded items). */
    total: number;
}

/** Minimal item contract — consumers render cards themselves. */
export interface KanbanItem {
    id: string;
    /** Current column id. */
    columnId: string;
}

export interface KanbanBoardProps<T extends KanbanItem> {
    columns: KanbanColumn[];
    items: T[];
    renderCard: (item: T) => ReactNode;
    /**
     * Whether an item may be dropped on a column (e.g. status-machine
     * transitions). Defaults to allowing every move.
     */
    canDrop?: (item: T, toColumnId: string) => boolean;
    /** Called after a successful drop on a different, allowed column. */
    onMove: (item: T, toColumnId: string) => void;
    /** Per-column "load more" (rendered when loaded < total). */
    onLoadMore?: (columnId: string) => void;
    loadingMoreColumnId?: string | null;
    disabled?: boolean;
    /** Replaces the default header (dot · label · count badge). */
    renderColumnHeader?: (column: KanbanColumn) => ReactNode;
    /** Rendered under the cards (e.g. "+ Add opportunity"). */
    renderColumnFooter?: (column: KanbanColumn) => ReactNode;
    /** Rendered in place of cards when a column has none loaded (e.g. a dashed drop hint). */
    renderEmptyColumn?: (column: KanbanColumn) => ReactNode;
    /** Columns whose headers can be dragged to reorder. */
    reorderableColumnIds?: readonly string[];
    /** Full new order of `reorderableColumnIds` after a header drag. */
    onReorderColumns?: (orderedIds: string[]) => void;
    /** Rendered after the last column (e.g. "+ Add stage"). */
    trailing?: ReactNode;
    /**
     * Renders `trailing` right after this column instead of at the end — the
     * opportunities board puts "+ Add stage" after the last OPEN stage, before
     * the Won/Lost columns. Unknown / omitted id ⇒ trailing goes last.
     */
    trailingAfterColumnId?: string;
}
